/**
 * Report runs (refreshable-report spec, Req 2, 4): the browser-side half of
 * executing a published report's defining question.
 *
 * A run is a fresh Ask AI turn the publish_report_runner Lambda enqueued
 * (`startReportRun`). Like the chat, the browser polls `getConversation` for
 * that turn (2 s interval, bounded by the request timeout) and then reports
 * the outcome with `recordReportRun`. Pure functions here; the dialog and the
 * dashboard plugin wire them to React state.
 */

import { getConversation } from "../../graphql/queries";
import { recordReportRun } from "../../graphql/mutations";
import type {
  getConversationInput,
  PublishedReportRun,
  PublishedReportSource,
  RecordReportRunInput,
  ReportRunScopeInput,
} from "../../models/API";
import { hexToString } from "../utils/utils";
import { parseStoredResponse } from "../utils/responseMarkers";
import { decodeRequestScope } from "./publishedReportModel";

/**
 * The minimal AppSync client surface the helpers need (generateAuditedClient()).
 * The Amplify client's `graphql` is overloaded (queries resolve to a Promise,
 * subscriptions to an Observable), so the return type is left open here.
 */
export interface GraphqlClient {
  graphql(options: { query: any; variables?: any }): any;
}

/** Outcome of one run, as read from its stored turn. */
export type RunOutcome =
  | {
      status: "succeeded";
      runTurnId: string;
      /** Marker-free answer markdown. */
      text: string;
      toolCalls?: any[];
      sources?: any[];
      requestScope?: PublishedReportSource["request_scope"];
    }
  | {
      status: "failed";
      runTurnId: string;
      /** The friendly failure text the backend stored. */
      text: string;
      errorType: string;
    }
  | { status: "timeout"; runTurnId: string }
  | { status: "cancelled"; runTurnId: string };

export interface PollOptions {
  /** Poll interval; 2 s like the chat. */
  intervalMs?: number;
  /** Give up after this long (the request timeout). */
  timeoutMs: number;
  /** Aborts the wait; the run itself keeps going server-side. */
  signal?: AbortSignal;
  /** Injected for tests. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Read the run turn once. `undefined` while the answer is not stored yet.
 * Conversations are keyed by user, so this only works for the user who
 * triggered the run (the one whose session the Lambda enqueued it under).
 */
export async function readRunTurn(
  client: GraphqlClient,
  userId: number,
  runTurnId: string
): Promise<RunOutcome | undefined> {
  const input: getConversationInput = {
    user_id: userId,
    conversation_id: runTurnId,
  };
  const result = await client.graphql({
    query: getConversation,
    variables: { input },
  });
  const turn = (result?.data?.getConversation ?? [])[0];
  if (!turn || !turn.response) return undefined;
  const parsed = parseStoredResponse(hexToString(turn.response));
  if (parsed.error) {
    return {
      status: "failed",
      runTurnId,
      text: parsed.text,
      errorType: parsed.error.type || "UnknownError",
    };
  }
  return {
    status: "succeeded",
    runTurnId,
    text: parsed.text,
    toolCalls: parsed.toolCalls,
    sources: parsed.sources,
    requestScope: decodeRequestScope(hexToString, turn.request_scope),
  };
}

/** Poll until the run turn is stored, the timeout passes, or `signal` aborts. */
export async function pollReportRun(
  client: GraphqlClient,
  userId: number,
  runTurnId: string,
  options: PollOptions
): Promise<RunOutcome> {
  const intervalMs = options.intervalMs ?? 2000;
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const deadline = now() + options.timeoutMs;
  while (now() < deadline) {
    await sleep(intervalMs);
    if (options.signal?.aborted) return { status: "cancelled", runTurnId };
    try {
      const outcome = await readRunTurn(client, userId, runTurnId);
      if (outcome) return outcome;
    } catch (err) {
      // Transient read failure: keep polling, like the chat does.
      console.error("[reportRun] poll failed:", err);
    }
    if (options.signal?.aborted) return { status: "cancelled", runTurnId };
  }
  return { status: "timeout", runTurnId };
}

/** Build the recordReportRun input for a finished outcome. */
export function buildRecordRunInput(args: {
  userId: number;
  reportId: number;
  runId?: number | null;
  outcome: RunOutcome;
  /** Publish-time runs (no runId) also record what was run, with what scope. */
  question?: string;
  runScope?: ReportRunScopeInput;
}): RecordReportRunInput {
  const base: RecordReportRunInput = {
    user_id: args.userId,
    report_id: args.reportId,
    run_turn_id: args.outcome.runTurnId,
    status: args.outcome.status === "succeeded" ? "succeeded" : "failed",
  };
  if (args.runId !== undefined && args.runId !== null) base.run_id = args.runId;
  if (args.question !== undefined) base.question = args.question;
  if (args.runScope !== undefined)
    base.request_scope = JSON.stringify(args.runScope);
  if (args.outcome.status === "succeeded") {
    base.content = args.outcome.text.trimEnd();
  } else {
    base.error_type =
      args.outcome.status === "failed"
        ? args.outcome.errorType
        : args.outcome.status === "timeout"
        ? "Timeout"
        : "Cancelled";
  }
  return base;
}

/** Record a finished run; returns the run row (or null when nothing came back). */
export async function recordRun(
  client: GraphqlClient,
  input: RecordReportRunInput
): Promise<PublishedReportRun | null> {
  const result = await client.graphql({
    query: recordReportRun,
    variables: { input },
  });
  return result?.data?.recordReportRun ?? null;
}
